import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchAllBarsIfNeeded } from '../../../actions/bars';
import { start, stop } from '../../../actions/stream';
import { selectChannel } from '../../../selectors/channelSelectors';
import useActions from '../../../utils/hooks/useActions';
import useSymbolMetadata from '../../../utils/hooks/useSymbolMetadata';
import { isJpeq } from '../../../utils/symbol';
import BarSizeSelector from './BarSizeSelector';
import './HistoricalTabComponent.scss';
import HistoricalTable from './HistoricalTable';

const BARS_LIMIT = 100;

const HistoricalTabComponent = ({ symbol }) => {
  const [startStream] = useActions([start], []);
  const [stopStream] = useActions([stop], []);
  const [fetchBars] = useActions([fetchAllBarsIfNeeded], []);

  const [barSize, setBarSize] = useState('1day');

  const symbolBars = useSelector((state) => state.bars || {});
  const symbolMetadata = useSymbolMetadata(symbol);
  const isSymbolEquity = isJpeq(symbolMetadata.assetClass);

  const channel = useMemo(() => `${symbol}/quote`, [symbol]);
  const quote = useSelector((state) => selectChannel(state, channel));

  useEffect(() => {
    if (!symbol) {
      return;
    }

    startStream(`${symbol}/quote`);
    fetchBars(symbol, barSize);

    return () => {
      stopStream(`${symbol}/quote`);
    };
  }, [symbol, barSize, fetchBars, startStream, stopStream]);

  const handleBarSizeChange = useCallback(
    (newBarSize) => {
      fetchBars(symbol, newBarSize);
      setBarSize(newBarSize);
    },
    [symbol, fetchBars]
  );

  // Determine loading/error states
  let isLoading, isError, noData, bars;

  const barsKey = `${symbol}/${barSize}`;
  const barsData = symbolBars[barsKey];

  if (!symbol || !barSize || !barsData || barsData.isLoading) {
    isLoading = true;
  } else if (barsData.isError) {
    isLoading = false;
    isError = true;
  } else {
    isLoading = false;
    isError = false;

    if (!barsData.data || !barsData.data.bars) {
      noData = true;
    } else {
      // Get bars and reverse (oldest to newest -> newest to oldest)
      bars = [...barsData.data.bars].reverse().slice(0, BARS_LIMIT);
    }
  }

  return (
    <div className="historical-tab" data-testid="tab-content-historical">
      <BarSizeSelector
        barSize={barSize}
        onBarSizeChange={handleBarSizeChange}
        bars={bars}
        precision={symbolMetadata.precision}
      />

      <HistoricalTable
        bars={bars}
        barSize={barSize}
        precision={symbolMetadata.precision}
        isLoading={isLoading}
        isError={isError}
        noData={noData}
        isSymbolEquity={isSymbolEquity}
        previousVolume={quote?.previousVolume || 0}
      />
    </div>
  );
};

export default HistoricalTabComponent;
